import { ReactElement, useCallback } from 'react';
import { AxiosResultDefaultError } from '../../../../api/request';
import { createFetchContext } from '../../../../components/contexts/FetchContextFactory';
import DisplayError from '../../../../components/info/DisplayError';
import { getSections } from '../api';
import { PerformanceAnalysisResponseType } from '../types';
import { usePerformanceAnalysisFiltersLoaderQuery } from './PerformanceAnalysisFiltersLoader';

export const ITEMS_PER_PAGE_DEFAULT = 10;

export interface PerformanceAnalysisListQuery {
  periodId: number;
  studyPlanVersionId?: number;
  courseId?: number;
  sectionId?: number;
  teacherNameOrId?: string;
  studentNameOrId?: string;
  page: number;
}

const { FetchProvider, FetchConsumer, useFetch } = createFetchContext<
  PerformanceAnalysisListQuery,
  PerformanceAnalysisResponseType,
  AxiosResultDefaultError
>();

export const usePerformanceAnalysisList = useFetch;

export function PerformanceAnalysisListConsumer({
  children,
}: {
  children: (args: {
    data?: PerformanceAnalysisResponseType;
    loading: boolean;
  }) => ReactElement;
}) {
  return (
    <FetchConsumer>
      {({ data, error, loading, refresh }) => {
        if (error) {
          return (
            <DisplayError
              insideCard
              textBody={error.code}
              retryAction={refresh}
              loadingAction={loading}
            />
          );
        }

        return children({ data, loading });
      }}
    </FetchConsumer>
  );
}

export function PerformanceAnalysisListLoader({
  children,
}: {
  children: ReactElement | ReactElement[];
}) {
  const { query } = usePerformanceAnalysisFiltersLoaderQuery();

  const request = useCallback(async (query: PerformanceAnalysisListQuery) => {
    return getSections({
      ...query,
      page: query.page - 1,
    });
  }, []);

  return (
    <FetchProvider
      request={request}
      defaultQuery={{ page: 1, periodId: query.periodId }}
      fetchImmediately={true}
      children={children}
    />
  );
}
